import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Eye } from 'lucide-react';
import Layout from '../components/Layout';
import { getMediaUrl } from '../utils/mediaUrl';
import OptimizedImage from '../components/OptimizedImage';
import { useSchemaOrg } from '../hooks/useSchemaOrg';
import { useCompanyInfo } from '../contexts/CompanyInfoContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DynamicPage = ({ pageType = 'tjeneste', slugOverride = null }) => {
  const params = useParams();
  const slug = slugOverride || params.slug;
  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (slug) {
      fetchPage();
    }
  }, [slug]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/pages/slug/${slug}`);
      setPage(response.data);
      setNotFound(false);
    } catch (error) {
      console.error('Error fetching page:', error);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const { name: companyName, publisher, organization, addressLine, phone, email, SITE_URL } = useSchemaOrg();

  const generateStructuredData = () => {
    if (!page) return null;
    const imageUrl = page.featured_image ? getMediaUrl(page.featured_image) : null;
    
    if (page.page_type === 'service_page') {
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": page.title,
        "description": page.meta_description || page.excerpt,
        "provider": publisher,
        "url": `${SITE_URL}/tjeneste/${page.slug}`,
        "image": imageUrl
      };
    } else if (page.page_type === 'news') {
      return {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": page.title,
        "description": page.meta_description || page.excerpt,
        "image": imageUrl,
        "datePublished": page.published_date || page.created_at,
        "dateModified": page.updated_at,
        "author": organization,
        "publisher": organization
      };
    } else if (page.page_type === 'campaign') {
      return {
        "@context": "https://schema.org",
        "@type": "Offer",
        "name": page.title,
        "description": page.meta_description || page.excerpt,
        "url": `${SITE_URL}/kampanje/${page.slug}`,
        "image": imageUrl,
        "seller": organization
      };
    }
    
    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": page.title,
      "description": page.meta_description || page.excerpt,
      "url": `${SITE_URL}/${page.slug}`,
      "isPartOf": { "@type": "WebSite", "name": companyName, "url": SITE_URL },
      "publisher": publisher,
      "mainEntity": {
        "@type": "Service",
        "name": page.title,
        "provider": organization
      }
    };
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-white text-xl">Laster...</div>
        </div>
      </Layout>
    );
  }

  if (notFound || !page) {
    return (
      <Layout>
        <div className="min-h-screen bg-black flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-white mb-4">404</h1>
            <p className="text-gray-400 mb-6">Siden ble ikke funnet</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-yellow-500 text-white rounded-lg hover:from-red-700 hover:to-yellow-600 transition-all"
            >
              Tilbake til forsiden
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const structuredData = generateStructuredData();

  return (
    <Layout>
      <Helmet>
        <title>{page.meta_title_override ? (page.meta_title || page.title) : `${page.meta_title || page.title} | Sand-tec AS`}</title>
        <meta name="description" content={page.meta_description || page.excerpt || ''} />
        {page.meta_keywords && <meta name="keywords" content={page.meta_keywords} />}
        
        {/* Open Graph */}
        <meta property="og:title" content={page.meta_title || page.title} />
        <meta property="og:description" content={page.meta_description || page.excerpt || ''} />
        <meta property="og:type" content={page.page_type === 'news' ? 'article' : 'website'} />
        <meta property="og:url" content={window.location.href} />
        {page.featured_image && <meta property="og:image" content={getMediaUrl(page.featured_image)} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.meta_title || page.title} />
        <meta name="twitter:description" content={page.meta_description || page.excerpt || ''} />
        {page.featured_image && <meta name="twitter:image" content={getMediaUrl(page.featured_image)} />}
        
        {/* Structured Data */}
        {structuredData && (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        )}
      </Helmet>

      {/* Comprehensive noscript fallback for AI agents, Bingbot and crawlers without JS */}
      <noscript>
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '30px 20px', fontFamily: 'system-ui, sans-serif', backgroundColor: '#fff', color: '#1a1a1a'}}>
          <header style={{borderBottom: '3px solid #dc2626', paddingBottom: '20px', marginBottom: '30px'}}>
            <h1 style={{fontSize: '2em', color: '#dc2626', margin: '0 0 8px'}}>{page?.title}</h1>
            {page?.excerpt && <p style={{fontSize: '1.1em', color: '#555', margin: 0}}>{page.excerpt}</p>}
            <p style={{fontSize: '0.85em', color: '#999', marginTop: '10px'}}>
              Publisert: {page?.created_at ? new Date(page.created_at).toLocaleDateString('no-NO', {year: 'numeric', month: 'long', day: 'numeric'}) : ''} | {page?.views || 0} visninger
            </p>
          </header>

          {page?.featured_image && (
            <div style={{marginBottom: '25px'}}>
              <img src={getMediaUrl(page.featured_image)} alt={page?.title} style={{maxWidth: '100%', height: 'auto', borderRadius: '8px'}} />
            </div>
          )}

          <article style={{marginBottom: '30px', lineHeight: '1.8', color: '#374151'}}>
            {page?.meta_description && <p style={{fontSize: '1.05em', marginBottom: '15px'}}>{page.meta_description}</p>}
            {page?.content && <div dangerouslySetInnerHTML={{__html: page.content}} />}
          </article>

          <section style={{marginBottom: '30px', padding: '20px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca'}}>
            <h2 style={{fontSize: '1.2em', color: '#dc2626', marginBottom: '10px'}}>Kontakt oss</h2>
            <p style={{margin: 0, color: '#374151'}}>{addressLine} — E-post: <a href={`mailto:${email}`} style={{color: '#dc2626'}}>{email}</a></p>
          </section>

          <nav style={{marginBottom: '30px', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px'}}>
            <h3 style={{fontSize: '1.1em', color: '#374151', marginBottom: '10px'}}>Se også</h3>
            <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
              <li><a href="/" style={{color: '#dc2626'}}>Forside</a></li>
              <li><a href="/tjenester" style={{color: '#dc2626'}}>Tjenester</a></li>
              <li><a href="/kampanjer" style={{color: '#dc2626'}}>Kampanjer</a></li>
              <li><a href="/aktuelt" style={{color: '#dc2626'}}>Aktuelt</a></li>
              <li><a href="/referanser" style={{color: '#dc2626'}}>Referanser</a></li>
              <li><a href="/anmeldelser" style={{color: '#dc2626'}}>Anmeldelser</a></li>
              <li><a href="/kontakt" style={{color: '#dc2626'}}>Kontakt</a></li>
            </ul>
          </nav>

          <footer style={{borderTop: '1px solid #e5e7eb', paddingTop: '15px', fontSize: '0.9em', color: '#6b7280'}}>
            <p>For maskinlesbar bedriftsinformasjon, se <a href="/api/llms.txt" style={{color: '#dc2626'}}>llms.txt</a> | <a href="/api/llms-full.txt" style={{color: '#dc2626'}}>llms-full.txt</a></p>
          </footer>
        </div>
      </noscript>

      <div className="min-h-screen bg-black">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-red-900/20 pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(239,68,68,0.1),transparent_50%)]"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Tilbake</span>
            </button>

            <div className="max-w-4xl">
              <h1 className="text-5xl sm:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-red-500 via-yellow-400 to-red-500 bg-clip-text text-transparent">
                  {page.title}
                </span>
              </h1>
              
              {page.excerpt && (
                <p className="text-xl text-gray-300 mb-6">
                  {page.excerpt}
                </p>
              )}

              <div className="flex items-center space-x-6 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(page.published_date || page.created_at).toLocaleDateString('no-NO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
                {page.updated_at && page.updated_at !== page.created_at && (
                  <div className="flex items-center space-x-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>Sist oppdatert: {new Date(page.updated_at).toLocaleDateString('no-NO', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Eye className="w-4 h-4" />
                  <span>{page.views} visninger</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Featured Image */}
        {page.featured_image && (
          <div className="container mx-auto px-4 -mt-10 mb-12 relative z-20">
            <div className="max-w-5xl mx-auto">
              <OptimizedImage
                src={page.featured_image}
                alt={page.title}
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                data-testid="featured-image"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="container mx-auto px-4 pb-20">
          <div className="max-w-4xl mx-auto">
            <article 
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-red-400 prose-a:no-underline hover:prose-a:text-red-300
                prose-strong:text-white
                prose-ul:text-gray-300 prose-ul:my-6
                prose-li:my-2
                prose-img:rounded-lg prose-img:shadow-lg"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />

            {/* CTA Section */}
            <div className="mt-16 p-8 bg-gradient-to-br from-red-900/20 to-yellow-900/20 border border-red-900/30 rounded-2xl">
              <h3 className="text-2xl font-bold text-white mb-4">
                Har du spørsmål?
              </h3>
              <p className="text-gray-300 mb-6">
                Kontakt oss i dag for en uforpliktende prat om hvordan vi kan hjelpe deg.
              </p>
              <div className="flex flex-wrap gap-4">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-yellow-500 text-white font-semibold rounded-lg hover:from-red-700 hover:to-yellow-600 transition-all shadow-lg"
                  >
                    Ring oss: {phone}
                  </a>
                )}
                <button
                  onClick={() => navigate('/kontakt')}
                  className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-all border border-white/20"
                >
                  Send melding
                </button>
              </div>
            </div>

            {/* Internal Links Section - Important for SEO and AI visibility */}
            <nav className="mt-12 p-6 bg-gray-800/50 border border-gray-700 rounded-xl" aria-label="Relatert innhold">
              <h3 className="text-xl font-bold text-white mb-4">
                Relaterte sider
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <li>
                  <a href="/" className="flex items-center text-gray-300 hover:text-red-400 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                    Tilbake til forsiden
                  </a>
                </li>
                <li>
                  <a href="/#services" className="flex items-center text-gray-300 hover:text-red-400 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                    Se alle våre tjenester
                  </a>
                </li>
                <li>
                  <a href="/#kontakt" className="flex items-center text-gray-300 hover:text-red-400 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                    Kontakt oss
                  </a>
                </li>
                <li>
                  <a href="/aktuelt" className="flex items-center text-gray-300 hover:text-red-400 transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2 rotate-180" />
                    Les flere artikler
                  </a>
                </li>
              </ul>
            </nav>

            {/* External Authority Links - Important for AI citation */}
            <aside className="mt-8 p-4 bg-blue-900/20 border border-blue-800/30 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-300 mb-2">
                Offisielle ressurser
              </h4>
              <p className="text-gray-400 text-sm">
                For mer informasjon om krav til EU-kontroll og fartsskrivere, se{' '}
                <a 
                  href="https://www.vegvesen.no/kjoretoy/eie-og-vedlikeholde/eu-kontroll/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  Statens vegvesen sine retningslinjer for EU-kontroll
                </a>.
              </p>
            </aside>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DynamicPage;
